<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50">
    <div class="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">

      <!-- En-tête -->
      <div class="px-8 pt-8 pb-4 text-center border-b border-gray-100">
        <Link :href="route('home')">
          <logo class="block w-48 mx-auto mb-4" />
        </Link>
        <h1 class="text-xl font-semibold text-gray-800">Changement de mot de passe requis</h1>
        <p class="mt-1 text-sm text-gray-500">
          Pour votre sécurité, veuillez définir un nouveau mot de passe avant de continuer.
        </p>
      </div>

      <!-- Formulaire -->
      <form @submit.prevent="submit" class="px-8 py-6 space-y-5">

        <!-- Nouveau mot de passe -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Nouveau mot de passe
          </label>
          <div class="relative">
            <input
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              class="w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              :class="form.errors.password ? 'border-red-400' : 'border-gray-300'"
              placeholder="Minimum 8 caractères"
            />
            <button type="button" @click="showPassword = !showPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <svg v-if="!showPassword" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            </button>
          </div>

          <!-- Indicateur de force -->
          <div class="mt-2 flex gap-1">
            <div
              v-for="i in 5" :key="i"
              class="h-1 flex-1 rounded-full transition-colors duration-300"
              :class="passwordStrength >= i ? strengthColor : 'bg-gray-200'"
            />
          </div>
          <p v-if="form.password" class="mt-1 text-xs" :class="strengthTextColor">
            {{ strengthLabel }}
          </p>

          <p v-if="form.errors.password" class="mt-1 text-xs text-red-500">
            {{ form.errors.password }}
          </p>
        </div>

        <!-- Confirmation -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Confirmer le mot de passe
          </label>
          <input
            v-model="form.password_confirmation"
            :type="showPassword ? 'text' : 'password'"
            class="w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            :class="confirmMismatch ? 'border-red-400' : 'border-gray-300'"
            placeholder="Répétez le mot de passe"
          />
          <p v-if="confirmMismatch" class="mt-1 text-xs text-red-500">
            Les mots de passe ne correspondent pas.
          </p>
        </div>

        <!-- Règles visuelles -->
        <ul class="grid grid-cols-2 gap-1 text-xs">
          <li :class="rules.length  ? 'text-green-600' : 'text-gray-400'">
            {{ rules.length  ? '✓' : '○' }} 8 caractères minimum
          </li>
          <li :class="rules.upper   ? 'text-green-600' : 'text-gray-400'">
            {{ rules.upper   ? '✓' : '○' }} Une majuscule
          </li>
          <li :class="rules.lower   ? 'text-green-600' : 'text-gray-400'">
            {{ rules.lower   ? '✓' : '○' }} Une minuscule
          </li>
          <li :class="rules.digit   ? 'text-green-600' : 'text-gray-400'">
            {{ rules.digit   ? '✓' : '○' }} Un chiffre
          </li>
          <li :class="rules.special ? 'text-green-600' : 'text-gray-400'">
            {{ rules.special ? '✓' : '○' }} Un caractère spécial
          </li>
        </ul>

        <button
          type="submit"
          :disabled="form.processing || !allRulesValid"
          class="w-full py-2.5 px-4 bg-green-600 hover:bg-green-700
                 disabled:opacity-50 disabled:cursor-not-allowed
                 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          {{ form.processing ? 'Enregistrement...' : 'Définir mon mot de passe' }}
        </button>

      </form>
    </div>
  </div>
</template>

<script>
import { useForm } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3'
import Logo from '@/Shared/Logo'

export default {
  components: {
    Logo,
    Link,
  },
  layout: null,

  setup() {
    const form = useForm({
      password:              '',
      password_confirmation: '',
    })
    return { form }
  },

  data() {
    return {
      showPassword: false,
    }
  },

  computed: {
    rules() {
      const p = this.form.password
      return {
        length:  p.length >= 8,
        upper:   /[A-Z]/.test(p),
        lower:   /[a-z]/.test(p),
        digit:   /\d/.test(p),
        special: /[@$!%*?&]/.test(p),
      }
    },
    passwordStrength() {
      return Object.values(this.rules).filter(Boolean).length
    },
    allRulesValid() {
      return Object.values(this.rules).every(Boolean) &&
             this.form.password === this.form.password_confirmation
    },
    confirmMismatch() {
      return this.form.password_confirmation.length > 0 &&
             this.form.password !== this.form.password_confirmation
    },
    strengthColor() {
      const colors = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-blue-400', 'bg-green-500']
      return colors[this.passwordStrength - 1] ?? 'bg-gray-200'
    },
    strengthTextColor() {
      const colors = ['text-red-500', 'text-orange-500', 'text-yellow-600', 'text-blue-600', 'text-green-600']
      return colors[this.passwordStrength - 1] ?? ''
    },
    strengthLabel() {
      const labels = ['', 'Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort']
      return labels[this.passwordStrength] ?? ''
    },
  },

  methods: {
    submit() {
      this.form.post(this.route('password.change.update'))
    },
  },
}
</script>